/**
 * Trip Payout Math (Frontend)
 *
 * IDENTICAL to backend lib/trip-math.ts. Mirrored on the frontend so the
 * Contract Form can show LIVE math preview while the user types — no
 * server round-trip needed for every keystroke.
 *
 * The backend remains the source of truth at trip-creation time, but for
 * UX preview, both implementations must produce identical numbers.
 */

export type RateUnit = 'per_m3' | 'per_trip' | 'per_ton' | 'per_km';

export interface TripMathInput {
  deliveredQty: number;
  targetQty: number;
  clientRateAtTarget: number;
  investorRateAtTarget: number;
  driverRateAtTarget: number;
  rateUnit?: RateUnit;
}

export interface TripMathResult {
  fraction: number;
  clientPayment: number;
  driverPay: number;
  investorGross: number;
  investorNet: number;
  companyKeep: number;
  totalDistributed: number;
  losses: {
    client: number;
    investor: number;
    driver: number;
    company: number;
  };
  valid: boolean;
  warnings: string[];
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

export function computeTripPayout(input: TripMathInput): TripMathResult {
  const warnings: string[] = [];

  if (input.targetQty <= 0) {
    return {
      fraction: 0, clientPayment: 0, driverPay: 0, investorGross: 0, investorNet: 0,
      companyKeep: 0, totalDistributed: 0,
      losses: { client: 0, investor: 0, driver: 0, company: 0 },
      valid: false, warnings: ['target_qty must be positive'],
    };
  }
  if (input.deliveredQty < 0) {
    return {
      fraction: 0, clientPayment: 0, driverPay: 0, investorGross: 0, investorNet: 0,
      companyKeep: 0, totalDistributed: 0,
      losses: { client: 0, investor: 0, driver: 0, company: 0 },
      valid: false, warnings: ['delivered_qty cannot be negative'],
    };
  }

  let effectiveDelivered = input.deliveredQty;
  let effectiveTarget = input.targetQty;
  if (input.rateUnit === 'per_trip') {
    effectiveDelivered = input.deliveredQty > 0 ? 1 : 0;
    effectiveTarget = 1;
  }

  let fraction = effectiveDelivered / effectiveTarget;
  if (fraction > 1.0) {
    warnings.push('Over-delivery capped at 100%');
    fraction = 1.0;
  }

  const clientPayment = round2(input.clientRateAtTarget * fraction);
  const driverPay     = round2(input.driverRateAtTarget * fraction);
  const clientShort   = round2(input.clientRateAtTarget * (1 - fraction));
  const investorGross = round2(input.investorRateAtTarget - clientShort);
  const investorNet   = round2(investorGross - driverPay);
  const companyKeep   = round2(input.clientRateAtTarget - input.investorRateAtTarget);
  const totalDistributed = round2(companyKeep + investorNet + driverPay);
  const validationDelta = Math.abs(totalDistributed - clientPayment);
  const valid = validationDelta < 0.02 && companyKeep >= 0 && input.investorRateAtTarget <= input.clientRateAtTarget;

  if (companyKeep < 0) {
    warnings.push('Investor rate exceeds client rate (negative company margin)');
  }
  if (investorNet < 0) {
    warnings.push('Investor net is negative for this scenario');
  }

  return {
    fraction,
    clientPayment,
    driverPay,
    investorGross,
    investorNet,
    companyKeep,
    totalDistributed,
    losses: {
      client:   round2(input.clientRateAtTarget - clientPayment),
      investor: round2(input.investorRateAtTarget - investorNet),
      driver:   round2(input.driverRateAtTarget - driverPay),
      company:  0,
    },
    valid,
    warnings,
  };
}

/**
 * Format SAR amount with 2 decimals.
 */
export function formatSar(amount: number | string | null | undefined, options?: { showCurrency?: boolean }): string {
  if (amount === null || amount === undefined || amount === '') return '—';
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (Number.isNaN(n)) return '—';
  const formatted = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return options?.showCurrency === false ? formatted : `${formatted} SAR`;
}
